import { Box, Typography, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../utils/i18n';

/**
 * Overlay that blurs content behind it and shows a sign-up CTA.
 * Wrap any content you want to gate:
 *
 *   <GuestWall visible={!isLoggedIn()}>
 *     <SensitiveContent />
 *   </GuestWall>
 *
 * Props:
 *  - visible: boolean — when true the wall is shown
 *  - children: the content to blur
 *  - height: optional min-height for the blurred area (default 200)
 *  - message: optional override message
 */
export default function GuestWall({ visible, children, height = 200, message }) {
  const navigate = useNavigate();
  const { t } = useI18n();

  if (!visible) return children;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: height }}>
      {/* Blurred content underneath */}
      <Box
        sx={{
          filter: 'blur(8px)',
          pointerEvents: 'none',
          userSelect: 'none',
          opacity: 0.5,
        }}
      >
        {children}
      </Box>

      {/* Overlay CTA */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          background: 'linear-gradient(180deg, rgba(18,18,18,0.3) 0%, rgba(18,18,18,0.85) 60%)',
          borderRadius: 1,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 40, mb: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mb: 0.5, textAlign: 'center', px: 2 }}>
          {message || t('guest.signUpToUnlock')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center', px: 2 }}>
          {t('guest.freeAccount')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" size="medium" onClick={() => navigate('/register')}>
            {t('guest.signUp')}
          </Button>
          <Button variant="outlined" size="medium" onClick={() => navigate('/login')}>
            {t('guest.signIn')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
